import PropTypes from 'prop-types'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Icons } from '@/theme/Images'
import { cn } from '@/lib/utils'

const SelectBlock = ({
  label,
  id,
  placeholder,
  formik,
  options = [],
  className,
  showValidation = true,
  onChange,
  description,
  ...props
}) => {
  const currentValue = formik.values[id] || ''
  const hasError = formik.errors[id] && formik.touched[id]

  const handleValueChange = value => {
    formik.setFieldValue(id, value)
    formik.setFieldTouched(id, true)
    if (onChange) onChange(value)
  }

  const handleOpenChange = open => {
    if (!open) {
      // Mark field as touched when dropdown closes (user clicked outside or selected an option)
      formik.setFieldTouched(id, true)
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm font-medium text-[--gray-700]">{label}</p>
      <div className="relative">
        <Select value={currentValue} onValueChange={handleValueChange} onOpenChange={handleOpenChange} {...props}>
          <SelectTrigger
            className={cn(
              'h-10 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'shadow-sm',
              currentValue ? 'text-[--gray-900] font-light' : 'text-[#667085] font-light',
              className
            )}
            style={{
              border: hasError
                ? '1px solid var(--Error-300, #FDA29B)'
                : className?.includes('border-red-500')
                  ? '1px solid #ef4444'
                  : '1px solid var(--Gray-300, #D0D5DD)',
            }}
            aria-label={label}
          >
            <SelectValue placeholder={placeholder} className="placeholder:text-[#667085] placeholder:font-normal" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectGroup>
              {options.map(option => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="hover:bg-[--gray-50] focus:bg-[--gray-100] focus:outline-none"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {showValidation && hasError && (
          <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Icons.FailureAlertIconSimple />
          </div>
        )}
      </div>
      {description && <p className="text-sm text-[--gray-600] font-normal mt-1">{description}</p>}
      {showValidation && hasError && <div className="text-red-500 text-sm mt-1.5">{formik.errors[id]}</div>}
    </div>
  )
}

SelectBlock.propTypes = {
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  formik: PropTypes.object.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  className: PropTypes.string,
  showValidation: PropTypes.bool,
  onChange: PropTypes.func,
  description: PropTypes.string,
}

export default SelectBlock
